import { memo, useMemo, useEffect } from 'react';
import { useNavigation } from '../../contexts/NavigationContext';
import ConnectionStatusBar from '../Debug/ConnectionStatusBar';
import QROverlay from '../Overlay/QROverlay';
import BottomTabBar from '../UISystem/Navigation/BottomTabBar';
import { navigateToQR } from '../../utils/navigationUtils';
import { hideInstantSplash } from '../../utils/instantSplash';
import { isFullTier } from '../../utils/userTier';
import { safariBleed } from '../../hooks/useShellChrome';
import './ScreenRouter.css';

// Screens where the tab bar is visible. Anon screens dropped — the Anon People
// page is a focused value preview (card + "Get Royale beta"), no abbreviated nav.
const TAB_SCREENS = new Set([
  'profile-connections-list',
  'profile-chat-list',
  'profile-posts',
  'profile-settings',
]);

// Screens that own their splash-hide moment: the exchange flow signals ready
// via App.handleAnonReady (instrumented by connectionTiming), and user-redirect
// resolves into it. Everything else hides the splash here when it renders.
const SPLASH_OWNED_SCREENS = new Set(['anon-exchange-flow', 'user-redirect']);

/**
 * Screen Router Component
 *
 * Renders the appropriate screen based on the current navigation state.
 * This centralizes all screen rendering logic and preserves component hierarchy
 * during navigation to maintain WebSocket connections.
 *
 * QROverlay is rendered alongside the current screen for Profile users.
 * It uses a mounted-but-hidden pattern — stays in DOM, controlled by isQROpen.
 *
 * BottomTabBar renders on tab screens — Profile (5 tabs) or Anon (3 tabs).
 *
 * PERFORMANCE: Wrapped in memo() to prevent re-renders when parent (NavigationProvider)
 * re-renders but screens/currentScreen/screenParams haven't changed.
 */
const ScreenRouter = memo(({ screens }) => {
  const { currentScreen, screenParams, isQROpen, showQR, hideQR, navigateTo } = useNavigation();

  // Compute showQROverlay dynamically based on profile data existence
  // This handles the case where user creates profile and becomes a profile user mid-session
  // Re-evaluated on each render (triggered by isQROpen changes from showQR())
  const showQROverlay = Boolean(localStorage.getItem('profile_user_data'));

  // User type for tab bar: profile if profile data exists, anon otherwise
  const userType = showQROverlay ? 'profile' : 'anon';

  // Show tab bar on tab screens — but hide it on the profile screen until the user
  // is actually a profile user. During signup an anon sits on 'profile-settings';
  // showing the (anon) tab bar there lets them wander off mid-signup (B1).
  // Full-only: a Simple profile gets NO tab bar at all (D5 chrome — Safari
  // chrome only; People header carries its profile access).
  const showTabBar =
    TAB_SCREENS.has(currentScreen) &&
    !(currentScreen === 'profile-settings' && userType === 'anon') &&
    (userType !== 'profile' || isFullTier());

  // Tab bar callbacks — memoized to avoid re-creating on every render
  const handleQRPress = useMemo(() => {
    if (userType === 'profile') {
      return () => navigateToQR(showQR);
    }
    // Anon: navigate to anon-connections-list with showSignup param
    return () => navigateTo('anon-connections-list', { showSignup: true }, true);
  }, [userType, showQR, navigateTo]);

  const handleNavigate = useMemo(() => (screen) => navigateTo(screen, {}, true), [navigateTo]);

  console.log(`SCREEN_ROUTER: Rendering screen "${currentScreen}" with params:`, screenParams);
  console.log('SCREEN_ROUTER: Available screens:', Object.keys(screens));
  console.log(
    'SCREEN_ROUTER: Is current screen available?',
    Object.prototype.hasOwnProperty.call(screens, currentScreen)
  );

  // Get the component for the current screen
  const ScreenComponent = screens[currentScreen];

  // People → Detail opening (DETAIL §9a, b/detail-opening): the colour grows
  // over People, which stays put underneath until Detail has arrived and is
  // back underneath at once on close. So while a Detail opened from People
  // (it carries `opening`) is up, People stays mounted beneath it — the same
  // instance, keyed in one stable layer, so going back keeps its scroll and
  // the tapped card is where the close lands. Every other change swaps as
  // before.
  const PeopleScreen = screens['new-people'];
  const peopleUnder = currentScreen === 'new-detail' && !!screenParams?.opening && !!PeopleScreen;
  const peopleLayer = (under, el) => (
    <div
      key="new-people"
      className={under ? 'screen-under' : 'screen-layer'}
      style={under ? { '--under-top': `${safariBleed()}px` } : undefined}
      inert={under || undefined}
      aria-hidden={under || undefined}
    >
      {el}
    </div>
  );

  // Hide the index.html instant splash once a real screen renders. The 'loading'
  // pseudo-screen has no component, so the splash keeps covering the boot phase.
  useEffect(() => {
    if (ScreenComponent && !SPLASH_OWNED_SCREENS.has(currentScreen)) {
      hideInstantSplash();
    }
  }, [ScreenComponent, currentScreen]);

  // If we have a component for this screen, render it with the params
  if (ScreenComponent) {
    console.log('SCREEN_ROUTER: Found component for screen:', currentScreen);

    return (
      <>
        <ConnectionStatusBar />
        {[
          peopleUnder ? peopleLayer(true, <PeopleScreen underlay />) : null,
          currentScreen === 'new-people' ? (
            peopleLayer(false, <ScreenComponent {...screenParams} />)
          ) : (
            <ScreenComponent key="screen" {...screenParams} />
          ),
        ]}
        {showTabBar && (
          <BottomTabBar
            currentScreen={currentScreen}
            userType={userType}
            onQRPress={handleQRPress}
            onNavigate={handleNavigate}
          />
        )}
        {showQROverlay && <QROverlay isOpen={isQROpen} onClose={hideQR} />}
      </>
    );
  }

  console.log('SCREEN_ROUTER: No component found for screen:', currentScreen);

  // Default loading screen if no matching screen component
  return (
    <>
      <ConnectionStatusBar />
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100%',
          backgroundColor: '#f5f5f5',
        }}
      >
        <div>
          {/* No loading text */}
          <p style={{ display: 'none' }}>Screen: {currentScreen || 'not set'}</p>
        </div>
      </div>
    </>
  );
});

ScreenRouter.displayName = 'ScreenRouter';

export default ScreenRouter;
